import { Typography, Link, Stack, Box } from '@mui/material';

import {
    formatDate,
    shipmentMonitoringCellTypographySx,
    shipmentMonitoringStackCellRootSx,
    ensureAbsoluteUrl,
    formatAirAliasQuantityWithUnit,
} from './shipmentMonitoringDefShared';
import { shipmentMonitoringClosureColumns } from './shipmentMonitoringClosureColumns';
import { getShipmentMonitoringIlStatusColumn } from './shipmentMonitoringIlStatusColumn';

export const getShipmentMonitoringTruckingColumns = (rows) => {
    return [
        {
            field: 'il_opus_reference_id',
            headerName: 'OPUS#',
            width: 85,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Typography sx={shipmentMonitoringCellTypographySx}>
                    {p.value ?? p.row.il_opus_reference_id ?? ''}
                </Typography>
            ),
        },
        {
            field: 'il_trade_partner_id_client',
            headerName: 'Client',
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Typography sx={shipmentMonitoringCellTypographySx}>
                    {typeof p.value === 'object' && p.value?.name
                        ? p.value.name
                        : p.row.il_trade_partner_id_client?.name || p.value || ''}
                </Typography>
            ),
        },
        {
            field: 'il_trade_partner_id_shipper',
            headerName: 'Shipper',
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.row.il_trade_partner_id_shipper || p.value || ''}
                    </Typography>
                );
            },
        },
        {
            field: 'il_trade_partner_id_consignee',
            headerName: 'Consignee',
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.row.il_trade_partner_id_consignee || p.value || ''}
                    </Typography>
                );
            },
        },
        {
            field: 'bol',
            headerName: 'BOL#',
            width: 105,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => <Typography sx={shipmentMonitoringCellTypographySx}>{p.value ?? ''}</Typography>,
        },
        {
            field: 'il_ftl_ltl_alias',
            headerName: 'Alias',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_ftl_ltl_alias || []).map((x) => x.alias);
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines.join('\n') || ' '}</Typography>;
            },
        },
        {
            field: 'volume',
            headerName: 'Vol',
            width: 72,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_ftl_ltl_alias ?? [])
                    .map((alias) => formatAirAliasQuantityWithUnit(alias))
                    .filter(Boolean)
                    .join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines || ' '}</Typography>;
            },
        },
        {
            field: 'il_location_id_pick_up',
            headerName: 'Pickup',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Pickup
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Location
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                const lines = (p.row.il_ftl_ltl_alias ?? []).map((alias) => alias.il_location_id_pick_up).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'il_location_id_deliver_to',
            headerName: 'Delivery Location',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Location
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                const lines = (p.row.il_ftl_ltl_alias ?? []).map((alias) => alias.il_location_id_deliver_to).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'etd',
            headerName: 'ETD',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.etd) || ' '}</Typography>
            ),
        },
        {
            field: 'eta',
            headerName: 'ETA',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.eta) || ' '}</Typography>
            ),
        },
        {
            field: 'customs',
            headerName: 'Customs',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_ftl_ltl_alias ?? []).map((alias, i) => {
                        const text = alias.customs ?? '';
                        const href = (alias.customs_link && String(alias.customs_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={alias.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={alias.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'delivery_order',
            headerName: 'Delivery Order',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Order
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_ftl_ltl_alias ?? []).map((alias, i) => {
                        const text = alias.delivery_order ?? '';
                        const href = (alias.delivery_order_link && String(alias.delivery_order_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={alias.id ?? i}
                                href={absoluteUrl}
                                component="a"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={alias.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'trucker',
            headerName: 'Trucker',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Stack sx={shipmentMonitoringStackCellRootSx}>
                        {(p.row.il_ftl_ltl_alias ?? []).map((alias, i) => {
                            const text = alias.il_trade_partner_id ?? '';
                            return (
                                <Typography key={alias.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                    {text}
                                </Typography>
                            );
                        })}
                    </Stack>
                );
            },
        },
        getShipmentMonitoringIlStatusColumn({ statusHouseHeaderStyle: 'ftl' }),
        {
            field: 'proof',
            headerName: 'Proof Delivery',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Proof
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_ftl_ltl_alias ?? []).map((alias, i) => {
                        const text = alias.proof ?? '';
                        const date = alias.proof_date ?? '';
                        const href = (alias.proof_link && String(alias.proof_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={alias.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={alias.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'invoice',
            headerName: 'Invoice',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_ftl_ltl_alias ?? []).map((alias, i) => {
                        const text = alias.invoice ?? '';
                        const date = alias.invoice_date ?? '';
                        const href = (alias.invoice_link && String(alias.invoice_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={alias.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={alias.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'crdr',
            headerName: 'CR/DR Note',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        CR/DR
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Note
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_ftl_ltl_alias ?? []).map((alias, i) => {
                        const text = alias.crdr ?? '';
                        const date = alias.crdr_date ?? '';
                        const href = (alias.crdr_link && String(alias.crdr_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={alias.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={alias.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        ...shipmentMonitoringClosureColumns,
    ];
};
